import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { url } from '../util/config';

const LoginComponent = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [fbUserData, setFbUserData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (localStorage.getItem('token')) { navigate('/dashboard'); }
    }, [navigate]);

    const submitFbUserData = async (fbData) => {
        console.log('submitFbUserData called with:', fbData);
        try {
            const dataPayload = fbData.data || fbData;
            const { phone_number_id, waba_id, business_id, ad_account_ids, page_ids, dataset_ids } = dataPayload;
            const payload = { phone_number_id, waba_id, business_id, ad_account_ids, page_ids, dataset_ids, type: fbData.type || 'default', event: fbData.event || null };
            console.log('Payload for fbuser endpoint:', payload);
            const res = await fetch(`${url}/fbuser`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            console.log('fbuser response status:', res.status);
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const responseData = await res.json();
            console.log('fbuser response data:', responseData);
            setFbUserData(responseData);
            return responseData;
        } catch (err) {
            console.error('Error submitting FB user data:', err);
            try {
                localStorage.setItem('fbUserDataFallback', JSON.stringify({ ...fbData, timestamp: new Date().toISOString(), fallback: true }));
                console.log('FB user data stored in fallback storage');
            } catch (storageErr) { console.error('Fallback storage failed:', storageErr); }
        }
    };

    useEffect(() => {
        console.log('Setting up Facebook SDK integration');
        const onMessage = (event) => {
            console.log('Received message event:', event.origin, event.data);
            if (!event.origin || !event.origin.endsWith('facebook.com')) { console.log('Ignoring message from non-Facebook origin'); return; }
            try {
                const data = JSON.parse(event.data);
                console.log('Parsed message data:', data);
                if (data.type === 'WA_EMBEDDED_SIGNUP' && data.data) { console.log('Handling WA_EMBEDDED_SIGNUP event:', data.event); submitFbUserData(data); }
            } catch (e) { console.log('Error parsing message data:', e, 'Raw data:', event.data); }
        };
        window.addEventListener('message', onMessage);
        console.log('Message listener attached');
        window.fbAsyncInit = function () {
            console.log('FB SDK loaded, initializing');
            try { window.FB.init({ appId: '1299149004568424', autoLogAppEvents: true, xfbml: true, version: 'v21.0' }); console.log('FB.init completed successfully'); }
            catch (e) { console.warn('FB.init failed', e); }
        };
        if (!document.querySelector('#facebook-jssdk')) {
            const s = document.createElement('script');
            s.id = 'facebook-jssdk'; s.async = true; s.defer = true; s.crossOrigin = 'anonymous';
            s.src = 'https://connect.facebook.net/en_US/sdk.js';
            document.body.appendChild(s);
        } else { console.log('Facebook SDK script already present'); }
        return () => { window.removeEventListener('message', onMessage); };
    }, []);

    const fbLoginCallback = async (response) => {
        console.log('fbLoginCallback called with response:', response);
        if (response?.authResponse?.code) {
            const code = response.authResponse.code;
            try {
                const payload = { code, config_id: '971800468657390' };
                const res = await fetch(`${url}/users/facebook-signup`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                if (!res.ok) { const err = await res.json().catch(() => ({ message: 'Signup failed' })); setErrorMessage(err.message || 'Facebook signup failed'); return; }
                const data = await res.json();
                localStorage.setItem('token', data.token);
                localStorage.setItem('role', JSON.stringify(data.role || {}));
                navigate('/dashboard');
            } catch (err) { console.error('FB signup error', err); setErrorMessage('Facebook signup failed. Try again.'); }
        } else { setErrorMessage('Facebook signup was not completed.'); }
    };

    const launchWhatsAppSignup = () => {
        console.log('launchWhatsAppSignup called');
        if (!window.FB) { setErrorMessage('Facebook SDK not loaded yet. Please try again in a moment.'); return; }
        window.FB.login(function (response) { fbLoginCallback(response); }, {
            config_id: '971800468657390', response_type: 'code', override_default_response_type: true,
            extras: { setup: { solutionID: '1903304463712014' }, feature: 'whatsapp_embedded_signup', sessionInfoVersion: '3', version: 3 }
        });
    };

    const validateEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) return 'Email is required.';
        if (!emailRegex.test(value)) return 'Please enter a valid email address.';
        return '';
    };

    const validatePassword = (value) => {
        if (!value) return 'Password is required.';
        if (value.length < 6) return 'Password must be at least 6 characters.';
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        const eErr = validateEmail(email);
        const pErr = validatePassword(password);
        setEmailError(eErr);
        setPasswordError(pErr);
        if (eErr || pErr) return;
        setIsLoading(true);
        try {
            const response = await fetch(`${url}/users/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
            const data = await response.json();
            if (response.ok) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('role', JSON.stringify(data.role));
                localStorage.setItem('uid', data.user._id);
                localStorage.setItem('balance', data.user.balance ?? 0);
                navigate('/dashboard');
            } else {
                setErrorMessage(data.error || data.message || 'Login failed. Please try again.');
            }
        } catch (error) {
            setErrorMessage('Unable to connect. Please check your connection and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.leftPanel}>
                    <div style={styles.brandArea}>
                        <img src="/logo.jpeg" alt="Social CRM Logo" style={styles.logo} />
                        <h1 style={styles.brandName}>Social CRM</h1>
                        <p style={styles.brandTagline}>WhatsApp Campaign Manager</p>
                    </div>
                    <div style={styles.featureList}>
                        {['Bulk WhatsApp Campaigns', 'Smart Contact Management', 'Flow Builder & Automation', 'Real-time Reporting'].map((f, i) => (
                            <div key={i} style={styles.featureItem}>
                                <span style={styles.featureDot}>✓</span>
                                <span>{f}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div style={styles.rightPanel}>
                    <h2 style={styles.formTitle}>Welcome back</h2>
                    <p style={styles.formSubtitle}>Sign in to your account</p>
                    <form onSubmit={handleSubmit} noValidate>
                        <div style={styles.fieldGroup}>
                            <label htmlFor="email" style={styles.label}>Email address</label>
                            <input id="email" type="email" value={email} placeholder="you@example.com" autoComplete="email"
                                style={{ ...styles.input, ...(emailError ? styles.inputError : {}) }}
                                onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(validateEmail(e.target.value)); }}
                                onBlur={() => setEmailError(validateEmail(email))} />
                            {emailError && <span style={styles.errorText}>{emailError}</span>}
                        </div>
                        <div style={styles.fieldGroup}>
                            <label htmlFor="password" style={styles.label}>Password</label>
                            <div style={styles.passwordWrapper}>
                                <input id="password" type={showPassword ? 'text' : 'password'} value={password}
                                    placeholder="Enter your password" autoComplete="current-password"
                                    style={{ ...styles.input, ...styles.passwordInput, ...(passwordError ? styles.inputError : {}) }}
                                    onChange={(e) => { setPassword(e.target.value); if (passwordError) setPasswordError(validatePassword(e.target.value)); }}
                                    onBlur={() => setPasswordError(validatePassword(password))} />
                                <button type="button" style={styles.eyeButton} onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                                    {showPassword ? '🙈' : '👁️'}
                                </button>
                            </div>
                            {passwordError && <span style={styles.errorText}>{passwordError}</span>}
                        </div>
                        {errorMessage && <div style={styles.errorBanner}><span>⚠️</span> {errorMessage}</div>}
                        <button type="submit" style={styles.submitBtn} disabled={isLoading}>
                            {isLoading ? <span style={styles.loadingContent}><span style={styles.spinner}></span> Signing in…</span> : 'Sign in'}
                        </button>
                        <div style={styles.divider}>
                            <span style={styles.dividerLine}></span>
                            <span style={styles.dividerText}>or</span>
                            <span style={styles.dividerLine}></span>
                        </div>
                        <button type="button" onClick={launchWhatsAppSignup} style={styles.fbBtn}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style={{ marginRight: 8 }}>
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                            </svg>
                            Sign up with Facebook
                        </button>
                    </form>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

const styles = {
    page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0d2b5e 0%, #1565c0 50%, #2388e0 100%)', padding: '20px', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" },
    card: { display: 'flex', width: '100%', maxWidth: '900px', minHeight: '520px', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.4)' },
    leftPanel: { flex: 1, background: 'linear-gradient(160deg, #2388e0 0%, #1565c0 100%)', padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: 'white' },
    brandArea: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' },
    logo: { height: '64px', width: '64px', borderRadius: '16px', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.5)', marginBottom: '8px' },
    brandName: { fontSize: '28px', fontWeight: '700', margin: 0, letterSpacing: '-0.5px' },
    brandTagline: { fontSize: '14px', opacity: 0.85, margin: 0 },
    featureList: { display: 'flex', flexDirection: 'column', gap: '14px' },
    featureItem: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', opacity: 0.92 },
    featureDot: { background: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 },
    rightPanel: { flex: 1, background: '#ffffff', padding: '48px 44px', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
    formTitle: { fontSize: '26px', fontWeight: '700', color: '#111827', margin: '0 0 6px' },
    formSubtitle: { fontSize: '14px', color: '#6b7280', margin: '0 0 32px' },
    fieldGroup: { marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
    input: { padding: '11px 14px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827' },
    inputError: { borderColor: '#ef4444' },
    passwordWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    passwordInput: { paddingRight: '44px' },
    eyeButton: { position: 'absolute', right: '12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: 0, lineHeight: 1 },
    errorText: { fontSize: '12px', color: '#ef4444' },
    errorBanner: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#dc2626', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' },
    submitBtn: { width: '100%', padding: '12px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', marginBottom: '20px' },
    loadingContent: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
    spinner: { width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' },
    divider: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' },
    dividerLine: { flex: 1, height: '1px', background: '#e5e7eb' },
    dividerText: { fontSize: '12px', color: '#9ca3af' },
    fbBtn: { width: '100%', padding: '11px', background: '#1877f2', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    footerNote: { textAlign: 'center', fontSize: '13px', color: '#9ca3af', marginTop: '24px', marginBottom: 0 },
};

export default LoginComponent;
